import React from 'react'
import { Link } from 'react-router-dom'

import makeStyles from '@mui/styles/makeStyles'
import {
  Divider,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Paper,
  LinearProgress,
} from '@mui/material'

import { useNavigatorOnline } from '@oieduardorabelo/use-navigator-online'

import { useRouter } from '@shared/hooks/use-router'
import { ACL } from '@shared/components/acl.component'
import { useACL } from '@shared/hooks/use-acl.hook'
import { useStore } from '@shared/hooks/use-store.hook'
import { UnstyledAnchor } from '@shared/components/unstyled-link.component'
import { Roles } from '@shared/types'
import { useAuth } from '@modules/auth/use-auth.hook'
import { LayoutAdminHeader } from './layout-admin-header.component'
import packageInfo from '../../../package.json'
import { useOnboardingStore } from '@shared/onboarding/onboarding.store'
import IDS from '@shared/onboarding/ids'
import { Actions } from '@shared/lib/reducer'
import { useIsFetching } from 'react-query'

type Props = {
  showIndicator: boolean
  children: React.ReactNode
}

export function Layout(props: Props) {
  const { dispatch } = useStore()
  const { resetAll, isAnyActive } = useOnboardingStore()
  const onboardingEnabled = isAnyActive()

  const { is } = useACL()
  const classes = useStyles()
  const { logout, authenticatedUser } = useAuth()
  const { status, isOffline } = useNavigatorOnline()
  const { location, query } = useRouter<{ companyID?: string }>()
  const isFetching = useIsFetching()
  const isClient = is([
    Roles.Client,
    Roles.ClientEditor,
    Roles.ClientAdministrator,
  ])

  const toggleOnboarding = () => {
    resetAll(!onboardingEnabled)
  }

  const adminLinkClassName = [
    classes.menuLink,
    isOffline ? classes.adminLinkDisabled : false,
  ]
    .filter(Boolean)
    .join(' ')

  const logoutClassNames = [
    classes.menuLink,
    classes.logoutLink,
    isClient ? classes.logoutLinkClient : false,
  ]
    .filter(Boolean)
    .join(' ')

  const handleLogout = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.includes('currentTab') || key.includes('scroll')) {
        localStorage.removeItem(key)
      }
    })

    dispatch({ type: Actions.RESET_VIEW_ROW_TABLE_FIELDS })

    logout()
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      {props.showIndicator && (
        <Typography className={classes.testEnv}>ŚRODOWISKO TESTOWE</Typography>
      )}
      <AppBar position="static" className={classes.appBar}>
        {isFetching > 0 && (
          <LinearProgress
            variant="indeterminate"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              zIndex: 9999,
              backgroundColor: 'transparent',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#32d46e',
              },
            }}
          />
        )}
        <Toolbar>
          <div className={classes.toolbarSectionLeft}>
            <Link to="/" className={classes.menuLink}>
              <img
                src="/logo/ecoms-esqula-logo-white.svg"
                className={classes.logoImage}
                alt="logo"
              />
              <Divider
                className={classes.topMenuDivider}
                orientation="vertical"
                flexItem
              />
              <Typography
                component="h1"
                variant="h6"
                color="white"
                noWrap
                className={classes.status}
              >
                Identyfikacja wymagań prawa
              </Typography>
            </Link>
          </div>
          <div className={classes.toolbarSectionRight}>
            {/**
             * @todo Duplication, move to `HeaderLink`
             */}
            {query.companyID ? (
              <ACL allowedRoles={[Roles.Client]}>
                <Link
                  to={`/history/${query.companyID}`}
                  className={[classes.menuLink, classes.topMenuLink].join(' ')}
                >
                  <Typography
                    variant="caption"
                    className={[
                      classes.title,
                      location.pathname.includes('history')
                        ? classes.topMenuLinkActive
                        : false,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    Historia
                  </Typography>
                </Link>
              </ACL>
            ) : null}
            <ACL
              allowedRoles={[
                Roles.ClientAdministrator,
                Roles.ClientEditor,
                Roles.Client,
                Roles.ClientBasic,
              ]}
            >
              <Typography
                variant="caption"
                color="white"
                sx={{
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onClick={toggleOnboarding}
              >
                {onboardingEnabled
                  ? 'Wyłącz przewodnik aplikacji'
                  : 'Włącz przewodnik aplikacji'}
              </Typography>
            </ACL>
            <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
              <Link
                to="/admin"
                className={adminLinkClassName}
                id={IDS.ADMINISTRATION_BUTTON}
              >
                <Typography
                  variant="caption"
                  color="white"
                  className={[
                    classes.title,
                    location.pathname.includes('/admin')
                      ? classes.topMenuLinkActive
                      : false,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  Administracja
                </Typography>
              </Link>
            </ACL>
            <ACL allowedRoles={[Roles.ClientAdministrator]}>
              <Link
                to="/client-admin"
                className={adminLinkClassName}
                id={IDS.ADMINISTRATION_BUTTON}
              >
                <Typography
                  variant="caption"
                  color="white"
                  className={[
                    classes.title,
                    location.pathname.includes('/client-admin')
                      ? classes.topMenuLinkActive
                      : false,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  Administracja
                </Typography>
              </Link>
            </ACL>

            {authenticatedUser && (
              <Typography
                className={logoutClassNames}
                variant="caption"
                onClick={handleLogout}
                color="white !important"
                sx={{
                  paddingLeft:
                    is([Roles.Admin]) ||
                    is([Roles.Auditor]) ||
                    is([Roles.ClientBasic])
                      ? '25px'
                      : '',
                }}
              >
                Wyloguj {authenticatedUser?.name} {authenticatedUser?.surname}
              </Typography>
            )}
          </div>
        </Toolbar>
        <LayoutAdminHeader />
      </AppBar>
      <main className={classes.content} id="container">
        <Container maxWidth="xl" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper className={classes.paper}>{props.children}</Paper>
            </Grid>
          </Grid>
        </Container>
      </main>
      <footer className={classes.footer}>
        <div className={classes.footerLinks}>
          <ul>
            <li>
              <UnstyledAnchor
                target="_blank"
                href="http://www.ecoms.pl/prawa-autorskie/"
              >
                Prawa autorskie
              </UnstyledAnchor>
            </li>
            <li>
              <UnstyledAnchor
                target="_blank"
                href="http://www.ecoms.pl/polityka-prywatnosci/"
              >
                Polityka prywatności
              </UnstyledAnchor>
            </li>
            <li>
              <UnstyledAnchor
                target="_blank"
                href="http://www.ecoms.pl/kontakt/"
              >
                Kontakt
              </UnstyledAnchor>
            </li>
            <li>Wersja: {packageInfo.version}</li>
          </ul>
          <div className={classes.footerCopyrights}>
            EcoMS Consulting Sp. z o.o. Copyright ©. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}

export const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  appBar: {
    backgroundColor: theme.palette.primary.main,
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  toolbarSectionLeft: {
    flex: 1,
  },
  toolbarSectionRight: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    flex: 1,
    display: 'flex',
    gap: 25,
  },

  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    textDecoration: 'none',
    lineHeight: theme.spacing(4.5),
  },
  status: {
    marginLeft: theme.spacing(1),
    lineHeight: theme.spacing(4.5),
  },
  appBarSpacer: theme.mixins.toolbar,
  appDoubleBarSpacer: Object.entries(theme.mixins.toolbar).reduce(
    (memo: { [key: string]: any }, [property, value]: [string, any]) => {
      const propertyValue = value as { [key: string]: any }

      memo[property] = propertyValue.minHeight
        ? { ...propertyValue, minHeight: propertyValue.minHeight * 2 }
        : propertyValue

      return memo
    },
    {} as { [key: string]: any }
  ),
  content: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
    paddingBottom: '100px',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
    },
  },
  container: {
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    minWidth: 1024,
    overflowX: 'auto',
  },
  fixedHeight: {
    height: 240,
  },
  logoImage: {
    position: 'relative',
    top: -2,
    height: '40px',
    marginRight: '10px',
  },
  menuLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
  },
  topMenuDivider: {
    opacity: 0.7,
    height: 25,
    top: 8,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
  },
  topMenuLink: {
    marginRight: 10,
    marginLeft: 10,
  },
  topMenuLinkActive: {
    fontWeight: 'bold',
  },
  adminLinkDisabled: {
    color: theme.palette.grey[300],
    pointerEvents: 'none',
  },
  logoutLink: {
    alignItems: 'center',
    cursor: 'pointer',
    borderLeft: '1px solid rgba(255,255,255,0.7)',
  },
  logoutLinkClient: {
    border: 'none',
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1200,
    '& > div': {
      display: 'flex',
      alignContent: 'space-between',
    },
    paddingTop: theme.spacing(1.75),
    paddingBottom: theme.spacing(1.75),
    paddingRight: theme.spacing(5),
    paddingLeft: theme.spacing(5),
    backgroundColor: theme.palette.grey['900'],
    color: theme.palette.grey['300'],
  },
  footerLinks: {
    '& ul': {
      display: 'flex',
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    '& li': {
      marginRight: theme.spacing(1),
    },
  },
  footerCopyrights: {
    flex: 1,
    textAlign: 'right',
  },
  testEnv: {
    padding: 10,
    textAlign: 'center',
    backgroundColor: 'red',
    color: 'white',
    fontWeight: 'bold',
  },
  onboarding: {
    flex: 1,
    textAlign: 'right',
    cursor: 'pointer',
  },
}))
